import React, { useState } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import NotificationToast from "./NotificationToast";
import { submitRegistration } from "../../../services/googleScriptService";
import { saveUserProfile } from "../../../utils/userStorage";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const INITIAL_FORM = {
  nom: "",
  prenom: "",
  sexe: "",
  cin_passeport: "",
  filiere: "",
  universite: "",
  situation: "",
  telephone: "",
};

export default function RegistrationForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validation du CIN (Doit commencer par NBE)
    if (!form.cin_passeport.toUpperCase().startsWith("NBE")) {
      setError("Le numéro de CIN / Passeport doit obligatoirement commencer par 'NBE'.");
      return;
    }

    // 2. Validation du Téléphone (Chiffres, espaces et + autorisés)
    const phoneRegex = /^\+?[0-9\s]+$/;
    if (!phoneRegex.test(form.telephone)) {
      setError("Le numéro de téléphone est invalide. Il ne doit contenir que des chiffres (ex: 0612345678 ou +2126...).");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      await submitRegistration(form);
      saveUserProfile(form);
      setSuccess(true);
      setForm(INITIAL_FORM);

      setTimeout(() => {
        setSuccess(false);
      }, 10000);
    } catch (err) {
      setError("Une erreur est survenue lors de l'envoi. Veuillez vérifier votre connexion et réessayer.");
      console.error(err);

      setTimeout(() => {
        setError(null);
      }, 10000);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-2.5 rounded-lg border border-acem-border bg-acem-surface-container-low text-acem-text focus:outline-none focus:ring-2 focus:ring-acem-primary/40 focus:border-acem-primary transition-all text-sm";
  const labelClass = "block text-xs font-bold text-acem-text mb-1.5 uppercase tracking-wider";

  return (
    <Card variant="elevated" className="max-w-2xl mx-auto p-6 sm:p-10">
      {/* Note d'autorisation restreinte aux résidents à Fès */}
      <div className="mb-6 p-4 rounded-xl bg-amber-50/90 border border-amber-200 text-amber-900 text-xs sm:text-sm font-medium flex items-start gap-3 shadow-sm">
        <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="leading-relaxed">
          <strong className="font-bold">Note importante :</strong> Seuls les étudiants et stagiaires comoriens domiciliés à <strong className="font-bold">Fès </strong> sont autorisés à remplir ce formulaire de recensement.
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="nom" className={labelClass}>Nom *</label>
            <input
              id="nom"
              name="nom"
              type="text"
              value={form.nom}
              onChange={handleChange}
              placeholder="Votre nom de famille"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="prenom" className={labelClass}>Prénom *</label>
            <input
              id="prenom"
              name="prenom"
              type="text"
              value={form.prenom}
              onChange={handleChange}
              placeholder="Votre prénom"
              required
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="sexe" className={labelClass}>Sexe *</label>
            <select
              id="sexe"
              name="sexe"
              value={form.sexe}
              onChange={handleChange}
              required
              className={inputClass}
            >
              <option value="">Sélectionnez...</option>
              <option value="masculin">Masculin</option>
              <option value="feminin">Féminin</option>
            </select>
          </div>
          <div>
            <label htmlFor="cin_passeport" className={labelClass}>Numéro CIN (Passeport) *</label>
            <input
              id="cin_passeport"
              name="cin_passeport"
              type="text"
              value={form.cin_passeport}
              onChange={handleChange}
              placeholder="Ex: NBEXXXXXX"
              required
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="filiere" className={labelClass}>Filière d'études</label>
          <input
            id="filiere"
            name="filiere"
            type="text"
            value={form.filiere}
            onChange={handleChange}
            placeholder="Ex: Médecine, Informatique, Économie..."
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="universite" className={labelClass}>Université / Établissement (si étudiant)</label>
          <input
            id="universite"
            name="universite"
            type="text"
            value={form.universite}
            onChange={handleChange}
            placeholder="Ex: FSDM, FST, Sup'Management, UEMF..."
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="situation" className={labelClass}>Profession / Situation (si non-étudiant)</label>
          <input
            id="situation"
            name="situation"
            type="text"
            value={form.situation}
            onChange={handleChange}
            placeholder="Ex: Salarié, Stagiaire, Touriste, Soins médicaux..."
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="telephone" className={labelClass}>Téléphone WhatsApp *</label>
          <input
            id="telephone"
            name="telephone"
            type="tel"
            value={form.telephone}
            onChange={handleChange}
            placeholder="Ex: +212 6XX XXXXXX"
            required
            className={inputClass}
          />
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={submitting}
          >
            {submitting ? "Envoi en cours…" : "Soumettre le formulaire de recensement"}
          </Button>
        </div>
      </form>

      {/* Notifications */}
      {success && (
        <NotificationToast
          type="success"
          message="L'information a été transmise avec succès au Bureau ACEM Fès."
          onClose={() => setSuccess(false)}
        />
      )}
      {error && (
        <NotificationToast
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}
    </Card>
  );
}
